import { Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";

export const useStyles = makeStyles((theme: Theme) => ({
  modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%) rotate(0deg)",
    display: "flex",
    flexDirection: "column",
    width: 650,
    maxHeight: "calc(100vh - 48px)",
    boxSizing: "border-box",
    borderRadius: 6,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.divider,
    opacity: 1,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 4px 24px rgba(37, 56, 88, 0.12)",
    outline: "none",
    direction: "rtl",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
    height: 56,
    gap: 10,
    paddingTop: 16,
    paddingRight: 24,
    paddingBottom: 16,
    paddingLeft: 24,
    boxSizing: "border-box",
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[400],
  },
  title: {
    fontFamily: theme.typography.fontFamily,
    fontSize: 16,
    fontWeight: 600,
    lineHeight: "24px",
    color: theme.palette.secondary.main,
    textAlign: "right",
    flex: 1,
    margin: 0,
  },
  closeButton: {
    flexShrink: 0,
    padding: 0,
    width: 24,
    height: 24,
    color: theme.palette.text.primary,
    "& .MuiSvgIcon-root": { fontSize: 20 },
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
    padding: 24,
    overflowY: "auto",
    backgroundColor: theme.palette.background.paper,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 24,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minWidth: 0,
  },
  label: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: 500,
    fontSize: 12,
    lineHeight: "140%",
    textAlign: "right",
    color: theme.palette.text.primary,
    margin: 0,
  },
  input: {
    width: "100%",
    margin: 0,
    "& .MuiOutlinedInput-root": {
      height: 48,
      boxSizing: "border-box",
      borderRadius: 4,
      gap: 10,
      backgroundColor: theme.palette.background.paper,
      fontFamily: theme.typography.fontFamily,
      fontWeight: 400,
      fontSize: 14,
      lineHeight: "21px",
      color: theme.palette.secondary.main,
      "& fieldset": {
        borderColor: theme.palette.divider,
        borderWidth: 1,
      },
      "&:hover fieldset": { borderColor: theme.palette.divider },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    },
    "& .MuiOutlinedInput-input": {
      height: "100%",
      boxSizing: "border-box",
      paddingTop: 12,
      paddingRight: 8,
      paddingBottom: 12,
      paddingLeft: 16,
      fontFamily: theme.typography.fontFamily,
      fontWeight: 400,
      fontSize: 14,
      lineHeight: "21px",
      textAlign: "right",
      color: theme.palette.secondary.main,
      "&::placeholder": {
        color: theme.palette.text.disabled,
        opacity: 1,
      },
    },
  },
  textArea: {
    width: "100%",
    margin: 0,
    opacity: 1,
    "& .MuiOutlinedInput-root": {
      height: 137,
      boxSizing: "border-box",
      borderRadius: 4,
      borderWidth: 1,
      gap: 10,
      opacity: 1,
      transform: "rotate(0deg)",
      alignItems: "flex-start",
      paddingTop: 16,
      paddingRight: 8,
      paddingBottom: 12,
      paddingLeft: 16,
      backgroundColor: theme.palette.background.paper,
      fontFamily: theme.typography.fontFamily,
      fontWeight: 400,
      fontSize: 14,
      lineHeight: "21px",
      color: theme.palette.secondary.main,
      "& fieldset": {
        borderColor: theme.palette.divider,
        borderWidth: 1,
      },
      "&:hover fieldset": { borderColor: theme.palette.divider },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    },
    "& .MuiOutlinedInput-input": {
      height: "auto !important",
      padding: 0,
      fontFamily: theme.typography.fontFamily,
      fontWeight: 400,
      fontSize: 14,
      lineHeight: "21px",
      textAlign: "right",
      color: theme.palette.secondary.main,
      resize: "none",
      overflow: "auto",
      "&::placeholder": {
        color: theme.palette.text.disabled,
        opacity: 1,
      },
    },
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flexShrink: 0,
    height: 68,
    gap: 12,
    paddingTop: 16,
    paddingRight: 24,
    paddingBottom: 16,
    paddingLeft: 24,
    boxSizing: "border-box",
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottomRightRadius: 6,
    borderBottomLeftRadius: 6,
    backgroundColor: theme.palette.grey[400],
  },
  cancelButton: {
    height: 36,
    padding: "15px 28px",
    borderRadius: 4,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.text.primary,
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1,
    textTransform: "none",
    backgroundColor: "transparent",
    boxShadow: "none",
    "&:hover": {
      borderColor: theme.palette.text.primary,
      backgroundColor: "transparent",
      boxShadow: "none",
    },
  },
  addButton: {
    height: 36,
    padding: "15px 28px",
    borderRadius: 4,
    border: "none",
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1,
    textTransform: "none",
    backgroundColor: theme.palette.primary.main,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      boxShadow: "none",
      border: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: `${theme.palette.divider} !important`,
      border: "none",
      color: `${theme.palette.text.disabled} !important`,
    },
  },
}));
